import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseAPI, examAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiFolder, FiVideo, FiFileText, FiFileText as FiExam, FiArrowLeft, FiPlus, FiEye, FiTrash2, FiDownload, FiEdit2, FiMove, FiLock, FiUnlock, FiX } from 'react-icons/fi';
import CustomVideoControls from '../components/CustomVideoControls';
import { BASE_URL } from '../config/config';
function ViewContent() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState('0');
  const [folders, setFolders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [exams, setExams] = useState([]);
  const [backFolderId, setBackFolderId] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [updateMessage, setUpdateMessage] = useState('');
  const [player, setPlayer] = useState(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCenterButton, setShowCenterButton] = useState(true);
  const playerIdRef = useRef(`youtube-player-${Date.now()}`);
  
  // Form visibility states (matching admin panel)
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [showAssignExamModal, setShowAssignExamModal] = useState(false);
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);
  
  // Edit mode states
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);

  // Video form data
  const [videoData, setVideoData] = useState({
    title: '',
    description: '',
    video_id: '',
    is_live: 'no',
    live_date: ''
  });
  
  // Document form data
  const [documentData, setDocumentData] = useState({
    doc_name: '',
    description: '',
    documents: [] // Changed to array to support multiple files
  });
  
  useEffect(() => {
    fetchCourseData();
  }, [courseId]);
  
  useEffect(() => {
    if (course) {
      loadFolderContents(currentFolderId, true);
    }
  }, [currentFolderId, course]);
  
  const fetchCourseData = async () => {
    try {
      const response = await courseAPI.viewContents(courseId);
      if (response.data.success) {
        setCourse(response.data.data.course);
      }
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load course data');
      setLoading(false);
    }
  };
  
  const loadFolderContents = async (folderId, onload = false) => {
    try {
      const params = {
        folderid: folderId,
        coursid: courseId,
        onload: onload ? '1' : ''
      };
      
      const response = await courseAPI.getDynamicFolder(params);
      if (response.data.success) {
        setFolders(response.data.data.folders || []);
        setVideos(response.data.data.videos || []);
        setDocuments(response.data.data.documents || []);
        setExams(response.data.data.exams || []);
        setBackFolderId(response.data.data.backFolder);
      }
    } catch (error) {
      toast.error('Failed to load folder contents');
    }
  };
  
  const handleFolderClick = (folderId) => {
    setCurrentFolderId(folderId);
  };
  
  const handleGoBack = () => {
    if (backFolderId !== null) {
      setCurrentFolderId(backFolderId);
    }
  };
  
  const handleDeleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    try {
      const params = {
        folderid: currentFolderId,
        coursid: courseId,
        type: type,
        id: id,
        status: 'delete'
      };
      
      const response = await courseAPI.getDynamicFolder(params);
      if (response.data.success) {
        toast.success('Item deleted successfully');
        loadFolderContents(currentFolderId);
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };
  
  const handleToggleStatus = async (type, id, currentStatus) => {
    try {
      const params = {
        folderid: currentFolderId,
        coursid: courseId,
        type: type,
        id: id,
        status: currentStatus
      };
      
      const response = await courseAPI.getDynamicFolder(params);
      if (response.data.success) {
        toast.success('Status updated successfully');
        loadFolderContents(currentFolderId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  const handleToggleDownloadStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus == 1 ? 0 : 1;
      const params = {
        folderid: currentFolderId,
        coursid: courseId,
        type: 'download_status',
        id: id,
        status: newStatus
      };
      
      const response = await courseAPI.getDynamicFolder(params);
      if (response.data.success) {
        toast.success('Download status updated');
        loadFolderContents(currentFolderId);
      }
    } catch (error) {
      toast.error('Failed to update download status');
    }
  };

  const handleRemoveExamFromFolder = async (courseExamId) => {
    if (!window.confirm('Remove this test series from folder?')) {
      return;
    }

    try {
      const response = await courseAPI.removeExamFromFolder(courseExamId);
      if (response.data.success) {
        toast.success('Test series removed successfully');
        loadFolderContents(currentFolderId);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove test series');
    }
  };
  
  const handlePlayVideo = (videoId) => {
    setSelectedVideo(videoId);
  };
  
  const closeVideoModal = () => {
    // Destroy player before closing modal
    if (player) {
      try {
        player.destroy();
      } catch (e) {
        console.log('Error destroying player:', e);
      }
      setPlayer(null);
    }
    setSelectedVideo(null);
    setIsPlayerReady(false);
    setIsPlaying(false);
    setShowCenterButton(true);
  };
  
  // Toggle video form visibility (matching admin panel behavior)
  const toggleVideoForm = () => {
    setShowVideoForm(!showVideoForm);
    setShowDocumentForm(false);
    setEditingVideoId(null);
    // Reset form
    setVideoData({
      title: '',
      description: '',
      video_id: '',
      is_live: 'no',
      live_date: ''
    });
  };
  
  // Handle edit video - pre-fill form
  const handleEditVideo = (video) => {
    setEditingVideoId(video.id);
    setVideoData({
      title: video.title || '',
      description: video.description || '',
      video_id: video.video_id || '',
      is_live: video.is_live || 'no',
      live_date: video.live_date || ''
    });
    setShowVideoForm(true);
    setShowDocumentForm(false);
    
    // Scroll to form
    setTimeout(() => {
      document.querySelector('.bg-white.rounded-lg.shadow-md')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // Toggle document form visibility
  const toggleDocumentForm = () => {
    setShowDocumentForm(!showDocumentForm);
    setShowVideoForm(false);
    setEditingDocId(null);
    // Reset form
    setDocumentData({
      doc_name: '',
      description: '',
      documents: []
    });
  };
  
  // Handle edit document - pre-fill form
  const handleEditDocument = (doc) => {
    setEditingDocId(doc.id);
    setDocumentData({
      doc_name: doc.doc_name || '',
      description: doc.description || '',
      documents: [] // Can't pre-fill file input for security reasons
    });
    setShowDocumentForm(true);
    setShowVideoForm(false);
    
    // Scroll to form
 

      setTimeout(() => {
      document.querySelector('.bg-white.rounded-lg.shadow-md')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // Handle video form submission
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoData.title || !videoData.video_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const data = {
        title: videoData.title,
        description: videoData.description,
        video_id: videoData.video_id,
        folder_id: currentFolderId,
        course_id: courseId,
        is_live: videoData.is_live,
        live_date: videoData.live_date
      };
      
      let response;
      if (editingVideoId) {
        // Update existing video
        response = await courseAPI.updateVideo(editingVideoId, data);
        if (response.data.success) {
          toast.success('Video updated successfully');
        }
      } else {
        // Add new video
        response = await courseAPI.addVideo(data);
        if (response.data.success) {
          toast.success('Video added successfully');
        }
      }
      
      setShowVideoForm(false);
      setEditingVideoId(null);
      setVideoData({
        title: '',
        description: '',
        video_id: '',
        is_live: 'no',
        live_date: ''
      });
      loadFolderContents(currentFolderId);
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${editingVideoId ? 'update' : 'add'} video`);
    }
  };
  
  // Handle document form submission
  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    
    // For editing, document file is optional (only update if new file selected)
    if (!documentData.doc_name || (!editingDocId && documentData.documents.length === 0)) {
      toast.error('Please fill in all required fields and select at least one document');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('doc_name', documentData.doc_name);
      formData.append('description', documentData.description);
      
      // Append multiple documents (if any selected)
      if (documentData.documents.length > 0) {
        for (let i = 0; i < documentData.documents.length; i++) {
          formData.append('document[]', documentData.documents[i]);
        }
      }
      
      formData.append('folder_id', currentFolderId);
      formData.append('course_id', courseId);
      
      let response;
      if (editingDocId) {
        // Update existing document
        response = await courseAPI.updateDocument(editingDocId, formData);
        if (response.data.success) {
          toast.success('Document updated successfully');
        }
      } else {
        // Add new document
        response = await courseAPI.addDocument(formData);
        if (response.data.success) {
          toast.success(`${documentData.documents.length} document(s) added successfully`);
        }
      }
      
      setShowDocumentForm(false);
      setEditingDocId(null);
      setDocumentData({
        doc_name: '',
        description: '',
        documents: []
      });
      loadFolderContents(currentFolderId);
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${editingDocId ? 'update' : 'add'} document`);
    }
  };
  
  // Extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    // Handle different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return url; // Return as-is if no pattern matches
  };
  
  // Load YouTube IFrame API and initialize player
  useEffect(() => {
    if (!selectedVideo) return;

    const initializePlayer = () => {
      const videoId = getYouTubeVideoId(selectedVideo);
      
      // Destroy existing player if any
      if (player) {
        try {
          player.destroy();
        } catch (e) {
          console.log('Error destroying player:', e);
        }
      }

      // Create new player
      const newPlayer = new window.YT.Player(playerIdRef.current, {
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: 0, // Hide default controls
          rel: 0,
          modestbranding: 1,
          fs: 0,
          disablekb: 1,
          iv_load_policy: 3,
          playsinline: 1,
          enablejsapi: 1,
        },
        events: {
          onReady: (event) => {
            setPlayer(event.target);
            setIsPlayerReady(true);
          },
          onStateChange: (event) => {
            // Track playing state for center button
            if (event.data === 1) { // Playing
              setIsPlaying(true);
              setShowCenterButton(false);
            } else if (event.data === 2) { // Paused
              setIsPlaying(false);
              setShowCenterButton(true);
            } else if (event.data === 0) { // Ended
              setIsPlaying(false);
              setShowCenterButton(true);
            }
          },
        },
      });
    };

    // Check if API is already loaded
    if (window.YT && window.YT.Player) {
      initializePlayer();
      return;
    }

    // Load the IFrame Player API code asynchronously
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      // API will call this function when ready
      window.onYouTubeIframeAPIReady = () => {
        initializePlayer();
      };
    }

    // Cleanup
    return () => {
      if (player) {
        try {
          player.destroy();
        } catch (e) {
          console.log('Cleanup error:', e);
        }
      }
    };
  }, [selectedVideo]);
  
  // Handle video overlay click
  const handleVideoClick = (e) => {
    if (e.button === 2) {
      // Right click - block it
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    
    // Left click - toggle play/pause
    if (player && e.button === 0) {
      try {
        const state = player.getPlayerState();
        if (state === 1) { // Playing
          player.pauseVideo();
          setIsPlaying(false);
          setShowCenterButton(true);
        } else {
          player.playVideo();
          setIsPlaying(true);
          setShowCenterButton(false);
        }
      } catch (err) {
        console.log('Player not ready');
      }
    }
  };
  
  // Prevent right-click and keyboard shortcuts on video player
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };
  
  // Comprehensive Security: Block all keyboard shortcuts that could reveal video URL
  useEffect(() => {
    if (!selectedVideo) return;
    
    const handleKeyDown = (e) => {
      // Block F12 (Dev Tools)
      if (e.keyCode === 123) {
        e.preventDefault();
        toast.warning('This action is disabled for security reasons');
        return false;
      }
      
      // Block Ctrl+Shift+I (Dev Tools)
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.key === 'I')) {
        e.preventDefault();
        toast.warning('Developer tools are disabled');
        return false;
      }
      
      // Block Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 74 || e.key === 'J')) {
        e.preventDefault();
        toast.warning('Console is disabled');
        return false;
      }
      
      // Block Ctrl+U (View Source)
      if (e.ctrlKey && (e.keyCode === 85 || e.key === 'u')) {
        e.preventDefault();
        toast.warning('View source is disabled');
        return false;
      }
      
      // Block Ctrl+S (Save Page)
      if (e.ctrlKey && (e.keyCode === 83 || e.key === 's')) {
        e.preventDefault();
        toast.warning('Saving is disabled');
        return false;
      }
      
      // Block Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 67 || e.key === 'C')) {
        e.preventDefault();
        toast.warning('Inspect element is disabled');
        return false;
      }
      
      // Block F11 (Fullscreen) - prevents direct YouTube access
      if (e.keyCode === 122 || e.key === 'F11') {
        e.preventDefault();
        return false;
      }
      
      // Block Cmd+Option+I (Mac Dev Tools)
      if (e.metaKey && e.altKey && (e.keyCode === 73 || e.key === 'i')) {
        e.preventDefault();
        toast.warning('Developer tools are disabled');
        return false;
      }
      
      // Block Cmd+Option+J (Mac Console)
      if (e.metaKey && e.altKey && (e.keyCode === 74 || e.key === 'j')) {
        e.preventDefault();
        toast.warning('Console is disabled');
        return false;
      }
      
      // Block Cmd+Option+C (Mac Inspect)
      if (e.metaKey && e.altKey && (e.keyCode === 67 || e.key === 'c')) {
        e.preventDefault();
        toast.warning('Inspect element is disabled');
        return false;
      }
      
      // Block PrintScreen
      if (e.keyCode === 44) {
        e.preventDefault();
        toast.warning('Screenshots are disabled');
        return false;
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Log if user switches tabs while video is playing (potential screen recording)
        console.warn('Tab switched during video playback');
      }
    };
    
    // Block right-click globally when video is playing
    const blockContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    
    // Block text selection and copying
    const blockCopy = (e) => {
      e.preventDefault();
      return false;
    };
    
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('contextmenu', blockContextMenu, true);
    document.addEventListener('copy', blockCopy, true);
    document.addEventListener('cut', blockCopy, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', blockContextMenu, true);
      document.removeEventListener('copy', blockCopy, true);
      document.removeEventListener('cut', blockCopy, true);
    };
  }, [selectedVideo]);
  
  // Drag and Drop Handlers
  const handleDragStart = (e, item, type) => {
    setDraggedItem({ item, type });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };
  
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDrop = async (e, dropIndex, type) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.type !== type) return;
    
    const items = type === 'folder' ? [...folders] : type === 'video' ? [...videos] : [...documents];
    const dragIndex = items.findIndex(item => item.id === draggedItem.item.id);
    
    if (dragIndex === dropIndex) return;
    
    // Reorder array
    const [removed] = items.splice(dragIndex, 1);
    items.splice(dropIndex, 0, removed);
    
    // Update state
    if (type === 'folder') {
      setFolders(items);
    } else if (type === 'video') {
      setVideos(items);
    } else {
      setDocuments(items);
    }
    
    // Send to server
    const arrayName = type === 'folder' ? 'arrayorder' : type === 'video' ? 'videoarrayorder' : 'documentarrayorder';
    const orderData = {
      [arrayName]: items.map(item => item.id)
    };
    
    try {
      const response = await courseAPI.updateOrder(orderData);
      if (response.data.success) {
        setUpdateMessage('Record updated successfully');
        setTimeout(() => setUpdateMessage(''), 2000);
      }
    } catch (error) {
      toast.error('Failed to update order');
      loadFolderContents(currentFolderId);
    }
    
    setDraggedItem(null);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  const hasContent = folders.length > 0 || videos.length > 0 || documents.length > 0 || exams.length > 0;
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            CONTENTS FOR {course?.title?.toUpperCase()}
          </h1>
          <button
            onClick={() => navigate(`/courses/${courseId}/addcontent`)}
            className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 transition-colors"
          >
            Back To Course
          </button>
        </div>
      </div>
      
      {/* Update Message */}
      {updateMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
          {updateMessage}
        </div>
      )}
      
      {/* Go Back Button */}
      {currentFolderId !== '0' && backFolderId !== null && (
        <div className="mb-4">
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <FiArrowLeft /> Go Back
          </button>
        </div>
      )}
      
      {/* Add Content Buttons (show when inside a folder - matching admin panel) */}
      {currentFolderId !== '0' && (
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <button
              onClick={toggleVideoForm}
              className={`px-4 py-2 ${showVideoForm ? 'bg-blue-700' : 'bg-blue-600'} text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium`}
            >
              <FiPlus /> Add Video
            </button>
            <button
              onClick={toggleDocumentForm}
              className={`px-4 py-2 ${showDocumentForm ? 'bg-blue-700' : 'bg-blue-600'} text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium`}
            >
              <FiPlus /> Add Documents
            </button>
            <button
              onClick={() => setShowAssignExamModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <FiPlus /> Add Test Series
            </button>
            <button
              onClick={() => navigate(`/courses/${courseId}/import?folderid=${currentFolderId}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <FiPlus /> Import Folders
            </button>
          </div>
          
          {/* Add/Edit Video Form (inline - matching admin panel) */}
          {showVideoForm && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 animate-fadeIn">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingVideoId ? 'Edit Video' : 'Add Videos'}
              </h3>
              <form onSubmit={handleVideoSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter video title"
                      value={videoData.title}
                      onChange={(e) => setVideoData({...videoData, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter youtube id (Ex - J3Q-QQfUkOY)"
                      value={videoData.video_id}
                      onChange={(e) => setVideoData({...videoData, video_id: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Desc. <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      placeholder="Enter description"
                      value={videoData.description}
                      onChange={(e) => setVideoData({...videoData, description: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Is Live. <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={videoData.is_live}
                      onChange={(e) => setVideoData({...videoData, is_live: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                    
                    {videoData.is_live === 'yes' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Live Date. <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="datetime-local"
                          value={videoData.live_date}
                          onChange={(e) => setVideoData({...videoData, live_date: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={toggleVideoForm}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Add/Edit Document Form (inline - matching admin panel) */}
          {showDocumentForm && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 animate-fadeIn">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingDocId ? 'Edit Document' : 'Add Documents'}
              </h3>
              <form onSubmit={handleDocumentSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter document name"
                      value={documentData.doc_name}
                      onChange={(e) => setDocumentData({...documentData, doc_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doc. {!editingDocId && <span className="text-red-500">*</span>}
                      {editingDocId && <span className="text-sm text-gray-500">(Optional - leave empty to keep current file)</span>}
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.rar"
                      multiple
                      onChange={(e) => setDocumentData({...documentData, documents: Array.from(e.target.files)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={!editingDocId}
                    />
                    {documentData.documents.length > 0 && (
                      <p className="mt-2 text-sm text-gray-600">
                        {documentData.documents.length} file(s) selected
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Desc. <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      placeholder="Enter description"
                      value={documentData.description}
                      onChange={(e) => setDocumentData({...documentData, description: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={toggleDocumentForm}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
      
      {/* Empty State */}
      {!hasContent && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <h3 className="text-xl text-gray-600">
            You haven't uploaded any content yet. Select the content from the right panel to start adding here
          </h3>
        </div>
      )}
      
      {/* Folders - List Style with Drag and Drop */}
      {folders.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Folders</h3>
            <p className="text-sm text-gray-500 mt-1">Drag and drop to reorder folders</p>
          </div>
          <div className="p-6">
            <ul className="space-y-2">
              {folders.map((folder, index) => (
                <li
                  key={folder.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, folder, 'folder')}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index, 'folder')}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-move bg-white"
                >
                  <div className="flex items-center gap-4">
                    <FiMove className="text-gray-400 flex-shrink-0 cursor-move" size={20} />
                    <FiFolder 
                      className="text-yellow-500 text-4xl flex-shrink-0 cursor-pointer" 
                      onClick={() => handleFolderClick(folder.folder_id)}
                    />
                    <div className="flex-1 cursor-pointer" onClick={() => handleFolderClick(folder.folder_id)}>
                      <p className="font-medium text-gray-900 text-lg hover:text-blue-600">{folder.folders}</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {folder.video_count > 0 && (
                          <span className="text-sm text-gray-600">
                            {folder.video_count} (Videos)
                          </span>
                        )}
                        {folder.document_count > 0 && (
                          <span className="text-sm text-gray-600">
                            {folder.document_count} (Files)
                          </span>
                        )}
                        {folder.exam_count > 0 && (
                          <span className="text-sm text-gray-600">
                            {folder.exam_count} (Test Series)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingFolder(folder);
                          setShowEditFolderModal(true);
                        }}
                        className="p-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                        title="Edit Folder"
                      >
                        <FiEdit2 size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteItem('folder', folder.id);
                        }}
                        className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                        title="Delete Folder"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      {/* Videos - List Style with Drag and Drop */}
      {videos.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Videos</h3>
            <p className="text-sm text-gray-500 mt-1">Drag and drop to reorder videos</p>
          </div>
          <div className="p-6">
            <ul className="space-y-3">
              {videos.map((video, index) => (
                <li
                  key={video.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, video, 'video')}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index, 'video')}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 transition-all cursor-move flex items-center gap-4 bg-white"
                >
                  <FiMove className="text-gray-400 flex-shrink-0" size={20} />
                  <img 
                    src={`http://i3.ytimg.com/vi/${video.video_id}/0.jpg`}
                    alt={video.title}
                    className="w-24 h-16 object-cover rounded flex-shrink-0 cursor-pointer"
                    onClick={() => handlePlayVideo(video.video_id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Title: {video.title}</p>
                    <p className="text-sm text-gray-500">Description: {video.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Lock/Unlock Status Display */}
                    <div className="flex items-center gap-1 text-sm">
                      {video.status == 1 ? (
                        <>
                          <FiUnlock className="text-green-600" size={16} />
                          <span className="text-gray-700 font-medium">Unlocked</span>
                        </>
                      ) : (
                        <>
                          <FiLock className="text-red-600" size={16} />
                          <span className="text-gray-700 font-medium">Locked</span>
                        </>
                      )}
                    </div>
                    
                    {/* Toggle Lock/Unlock Button */}
                    <button
                      onClick={() => handleToggleStatus('video', video.id, video.status)}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      title={video.status == 1 ? 'Click to Lock (Paid)' : 'Click to Unlock (Free)'}
                    >
                      {video.status == 0 ? (
                        <FiUnlock className="text-green-600" size={16} />
                      ) : (
                        <FiLock className="text-gray-600" size={16} />
                      )}
                    </button>
                    
                    {/* Edit Button */}
                    <button
                      onClick={() => handleEditVideo(video)}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Edit Video"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteItem('video', video.id)}
                      className="p-2 bg-gray-100 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      {/* Documents - List Style with Drag and Drop */}
      {documents.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
            <p className="text-sm text-gray-500 mt-1">Drag and drop to reorder documents</p>
          </div>
          <div className="p-6">
            <ul className="space-y-3">
              {documents.map((doc, index) => (
                <li
                  key={doc.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, doc, 'document')}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index, 'document')}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 transition-all cursor-move flex items-center gap-4 bg-white"
                >
                  <FiMove className="text-gray-400 flex-shrink-0" size={20} />
                  <a 
                    href={`http://localhost/brainershub/${doc.document}`}
                    {...(doc.download_status == 1 ? { download: true } : { target: '_blank', rel: 'noopener noreferrer' })}
                    className="flex-shrink-0"
                  >
                    {doc.download_status == 1 ? (
                      <FiDownload className="text-blue-500 text-4xl" />
                    ) : (
                      <FiFileText className="text-blue-500 text-4xl" />
                    )}
                  </a>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Title: {doc.doc_name}</p>
                    <p className="text-sm text-gray-500">Description: {doc.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Lock/Unlock Status Display */}
                    <div className="flex items-center gap-1 text-sm">
                      {doc.status == 1 ? (
                        <>
                          <FiUnlock className="text-green-600" size={16} />
                          <span className="text-gray-700 font-medium">Unlocked</span>
                        </>
                      ) : (
                        <>
                          <FiLock className="text-red-600" size={16} />
                          <span className="text-gray-700 font-medium">Locked</span>
                        </>
                      )}
                    </div>
                    
                    {/* Toggle Lock/Unlock Button */}
                    <button
                      onClick={() => handleToggleStatus('doc', doc.id, doc.status)}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      title={doc.status == 1 ? 'Click to Lock (Paid)' : 'Click to Unlock (Free)'}
                    >
                      {doc.status == 0 ? (
                        <FiUnlock className="text-green-600" size={16} />
                      ) : (
                        <FiLock className="text-gray-600" size={16} />
                      )}
                    </button>
                    
                    {/* Toggle Download Status Button */}
                    <button
                      onClick={() => handleToggleDownloadStatus(doc.id, doc.download_status)}
                      className={`p-2 rounded-lg transition-colors ${
                        doc.download_status == 1
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      title={doc.download_status == 1 ? 'Downloadable (Click to disable)' : 'View Only (Click to enable download)'}
                    >
                      {doc.download_status == 0 ? (
                        <FiFileText className="text-gray-600" size={16} />
                      ) : (
                        <FiDownload className="text-blue-600" size={16} />
                      )}
                    </button>
                    
                    {/* Edit Button */}
                    <button
                      onClick={() => handleEditDocument(doc)}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Edit Document"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDeleteItem('doc', doc.id)}
                      className="p-2 bg-gray-100 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      
      {/* Exams */}
      {exams.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Test Series</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {exams.map((exam) => (
                <div
                  key={exam.course_exam_id}
                  className="border border-gray-200 rounded-lg p-5 hover:border-blue-400 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <FiExam className="text-purple-500 text-3xl flex-shrink-0 mt-1" />
                    
                    <div className="flex-1">
                      {/* Title and Badges */}
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900 text-lg">{exam.exam}</h4>
                        {exam.is_quiz == '1' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            Quiz
                          </span>
                        )}
                        {exam.is_live == '1' && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded animate-pulse">
                            🔴 Live
                          </span>
                        )}
                        {exam.is_active == 1 ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      {/* Description */}
                      {exam.description && (
                        <p className="text-sm text-gray-600 mb-3">{exam.description}</p>
                      )}
                      
                      {/* Exam Details Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Duration</p>
                          <p className="font-semibold text-gray-900">{exam.duration}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Total Marks</p>
                          <p className="font-semibold text-gray-900">{exam.marks || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Passing %</p>
                          <p className="font-semibold text-gray-900">{exam.passing_percentage}%</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Attempts</p>
                          <p className="font-semibold text-gray-900">{exam.attempt}</p>
                        </div>
                      </div>
                      
                      {/* Date Range */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">From:</span>
                          <span>{new Date(exam.exam_from).toLocaleString('en-IN', { 
                            dateStyle: 'medium', 
                            timeStyle: 'short' 
                          })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">To:</span>
                          <span>{new Date(exam.exam_to).toLocaleString('en-IN', { 
                            dateStyle: 'medium', 
                            timeStyle: 'short' 
                          })}</span>
                        </div>
                      </div>
                      
                      {/* Additional Info */}
                      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                        {exam.is_neg_marking == 1 && (
                          <span className="px-2 py-1 bg-red-50 text-red-600 rounded">
                            Negative Marking: -{exam.negative_marks}
                          </span>
                        )}
                        {exam.is_random_question == 1 && (
                          <span className="px-2 py-1 bg-yellow-50 text-yellow-600 rounded">
                            Random Questions
                          </span>
                        )}
                        {exam.publish_result == '1' && (
                          <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded">
                            Auto Publish Results
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => navigate(`/exams/${exam.id}/questions`)}
                        className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
                        title="View Exam Details & Questions"
                      >
                        <FiEye size={16} />
                        <span className="text-sm font-medium">View</span>
                      </button>
                      <button
                        onClick={() => handleRemoveExamFromFolder(exam.course_exam_id)}
                        className="px-4 py-2 text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                        title="Remove from Folder"
                      >
                        <FiTrash2 size={16} />
                        <span className="text-sm font-medium">Remove</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Secure Video Modal (Using YouTube IFrame API - Matching Quiz-App) */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center video-modal-secure overflow-y-auto"
          style={{ 
            zIndex: 9999,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            padding: '2px 2px'
          }}
          onContextMenu={handleContextMenu}
        >
          {/* Fixed Close Button - Top Right */}
          <button
            onClick={closeVideoModal}
            className="fixed top-1 right-1 md:top-1 md:right-1 z-[10000] bg-red-600 hover:bg-red-700 text-white rounded-full p-2 md:p-2 transition-all shadow-2xl hover:scale-110"
            title="Close Video"
            style={{ userSelect: 'none' }}
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div 
            className="w-full max-w-7xl mx-auto"
            style={{ 
              maxWidth: '95vw'
            }}
          >
            {/* Header with Title */}
            <div className="mb-2 md:mb-3">
              <h3 className="text-lg md:text-xl font-semibold text-white text-center">Play Video</h3>
            </div>
            
            {/* Video Container */}
            <div 
              className="video-container relative w-full rounded-lg overflow-hidden bg-black shadow-2xl" 
              style={{ 
                height: 'calc(100vh)',
                maxHeight: '650px',
                minHeight: '300px'
              }}
            >
              {/* YouTube Player Container */}
              <div 
                id={playerIdRef.current}
                className="absolute top-0 left-0 w-full h-full"
              />
              
              {/* Full transparent overlay to block right-click and handle clicks */}
              <div 
                className="absolute top-0 left-0 w-full h-full z-[15] cursor-pointer"
                style={{ 
                  pointerEvents: 'auto',
                  background: 'transparent'
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onClick={handleVideoClick}
                onMouseDown={(e) => {
                  if (e.button === 2) {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }
                }}
              >
                {/* Center Play/Pause Button with Skip Controls */}
                {showCenterButton && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none animate-fadeIn">
                    <div className="flex items-center gap-4 md:gap-8">
                      {/* Skip Backward 15s */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (player) {
                            const currentTime = player.getCurrentTime();
                            const newTime = Math.max(0, currentTime - 15);
                            player.seekTo(newTime, true);
                          }
                        }}
                        className="pointer-events-auto bg-black/60 backdrop-blur-sm rounded-full p-3 md:p-4 transition-all duration-300 hover:bg-black/80 hover:scale-110 shadow-2xl"
                        aria-label="Skip backward 15 seconds"
                      >
                        <div className="relative">
                          <svg className="w-8 h-8 md:w-10 md:h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                          </svg>
                          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] md:text-xs font-bold text-white">
                            15
                          </span>
                        </div>
                      </button>

                      {/* Play/Pause Button */}
                      <div className="pointer-events-auto bg-black/60 backdrop-blur-sm rounded-full p-4 md:p-6 transition-all duration-300 hover:bg-black/80 hover:scale-110 shadow-2xl">
                        {isPlaying ? (
                          <svg 
                            className="w-12 h-12 md:w-16 md:h-16 text-white"
                            fill="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                          </svg>
                        ) : (
                          <svg 
                            className="w-12 h-12 md:w-16 md:h-16 text-white" 
                            fill="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        )}
                      </div>

                      {/* Skip Forward 15s */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (player) {
                            const currentTime = player.getCurrentTime();
                            const duration = player.getDuration();
                            const newTime = Math.min(duration, currentTime + 15);
                            player.seekTo(newTime, true);
                          }
                        }}
                        className="pointer-events-auto bg-black/60 backdrop-blur-sm rounded-full p-3 md:p-4 transition-all duration-300 hover:bg-black/80 hover:scale-110 shadow-2xl"
                        aria-label="Skip forward 15 seconds"
                      >
                        <div className="relative">
                          <svg className="w-8 h-8 md:w-10 md:h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
                          </svg>
                          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] md:text-xs font-bold text-white">
                            15
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Overlays to block YouTube logo and controls - Only on desktop */}
              {/* Top overlay - blocks YouTube logo and video title */}
              <div 
                className="hidden md:block absolute top-0 left-0 w-full h-[60px] bg-transparent z-20 pointer-events-none"
              />
              
              {/* Bottom-right overlay - blocks share button, settings, and other YouTube controls */}
              <div 
                className="hidden md:block absolute bottom-[60px] right-[10px] w-[200px] h-[45px] bg-transparent z-20 pointer-events-none"
              />
              
              {/* Top-right overlay - blocks YouTube logo in corner */}
              <div 
                className="hidden md:block absolute top-[10px] right-[10px] w-[100px] h-[40px] bg-transparent z-20 pointer-events-none"
              />
              
              {/* Custom Controls */}
              {isPlayerReady && player && <CustomVideoControls player={player} />}
            </div>
            
            {/* Footer Message */}
            <div className="mt-2 md:mt-3 text-center">
              <p className="text-xs md:text-sm text-white/80 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 inline-block">
                🔒 This video is protected. Sharing or downloading is prohibited.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Assign Exam Modal (matching admin panel) */}
      {showAssignExamModal && (
        <AssignExamModal
          show={showAssignExamModal}
          courseId={courseId}
          folderId={currentFolderId}
          onClose={() => {
            setShowAssignExamModal(false);
            loadFolderContents(currentFolderId);
          }}
        />
      )}
      
      {/* Edit Folder Modal */}
      {showEditFolderModal && (
        <EditFolderModal
          show={showEditFolderModal}
          folder={editingFolder}
          courseId={courseId}
          onClose={() => {
            setShowEditFolderModal(false);
            setEditingFolder(null);
            fetchCourseData();
            loadFolderContents(currentFolderId);
          }}
        />
      )}
    </div>
  );
}

// Assign Exam Modal Component (matching admin panel)
const AssignExamModal = ({ show, courseId, folderId, onClose }) => {
  const [exams, setExams] = useState([]);
  const [selectedExams, setSelectedExams] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchExams();
    }
  }, [show]);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const response = await examAPI.getExams();
      if (response.data.success) {
        setExams(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching exams');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignExams = async () => {
    if (selectedExams.length === 0) {
      toast.warning('Please select at least one exam');
      return;
    }

    try {
      // Assign multiple exams to the folder
      const promises = selectedExams.map(examId =>
        examAPI.assignToCourse({
          exam_id: examId,
          course_id: courseId,
          folder_id: folderId
        })
      );

      await Promise.all(promises);
      toast.success(`${selectedExams.length} test series assigned successfully`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error assigning exams');
    }
  };

  const toggleSelectExam = (examId) => {
    setSelectedExams(prev =>
      prev.includes(examId) ? prev.filter(id => id !== examId) : [...prev, examId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedExams.length === exams.length) {
      setSelectedExams([]);
    } else {
      setSelectedExams(exams.map(e => e.id));
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Add Test Series to Folder</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : exams.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No exams available. Please create exams first.</p>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={selectedExams.length === exams.length && exams.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Exam Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Duration</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Questions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {exams.map((exam) => (
                  <tr key={exam.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedExams.includes(exam.id)}
                        onChange={() => toggleSelectExam(exam.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm">{exam.exam}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{exam.duration_time} mins</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{exam.questions_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssignExams}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            disabled={selectedExams.length === 0}
          >
            Assign {selectedExams.length} Exam(s)
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Folder Modal Component
const EditFolderModal = ({ show, folder, courseId, onClose }) => {
  const [folderName, setFolderName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show && folder) {
      setFolderName(folder.title || folder.folders || '');
    }
  }, [show, folder]);

  const handleUpdateFolder = async (e) => {
    e.preventDefault();
    
    if (!folderName.trim()) {
      toast.error('Folder name cannot be empty');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        id: courseId,
        folder: folderName,
        folderid: folder.id || folder.folder_id
      };

      const response = await courseAPI.createMainFolder(data);
      if (response.data.success) {
     
        toast.success(response.data.message || 'Folder updated successfully');
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to update folder');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update folder');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Edit Folder</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleUpdateFolder}>
          <div className="p-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                required
                placeholder="Enter folder name"
                autoFocus
              />
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <FiEdit2 /> {submitting ? 'Updating...' : 'Update Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ViewContent;
